/**
 * Reviews Cross-Platform Synthetic Metrics (Story 23.7 + Story 23.8 — closes
 * Epic 23 in full)
 *
 * Why this module exists:
 *   No competitor reporting tool surfaces a single 3-platform reputation join
 *   (Google Reviews + Trustpilot + Yelp) verified May 2026 against
 *   AgencyAnalytics + Whatagraph + Databox + Birdeye + Podium + Reputation.com.
 *   Stories 23.1 / 23.3 / 23.5 each shipped a per-platform reviews silo;
 *   Story 23.7 cashes in the cross-platform join via 4 synthetic-aggregate
 *   reputation metrics + 4 widget presets — the agency-canonical answer to
 *   "how are we doing across our reputation footprint?" Story 23.8 extends
 *   with 4 reply-tracking synthetic-aggregate metrics + 4 widget presets —
 *   answering the agency-canonical SECOND question: "are we responding
 *   across our reputation footprint?"
 *
 * Architectural shape:
 *   All metrics are SYNTHETIC AGGREGATES — they do NOT correspond to
 *   wire-format API fields on any single review platform. They are computed
 *   at preset-expansion time from a join of the per-platform baselines.
 *   `apiField` is stamped `_synthetic_reviews_cross_platform_aggregate`
 *   so the build-time wire-field-must-match-API-field invariant test
 *   exempts them via `REVIEWS_CROSS_PLATFORM_SYNTHETIC_METRIC_KEYS`.
 *
 * Calibration: all 3 platforms use a 0-5 decimal rating scale → no
 * cross-platform normalization needed (unlike Story 20.9's SEO Cross-Tool
 * where Ahrefs DR + Semrush AS + Moz DA use proprietary models). The
 * `calibration.ts` companion module exports a lightweight footnote that
 * documents this directly-comparable property + the Yelp 3-excerpt cap.
 *
 * 2-platform vs 3-platform scope:
 *   Story 23.7's 4 metrics are 3-platform (GR + TP + Yelp). Story 23.8's
 *   4 metrics are 2-platform (GR + TP only — Yelp Fusion ships no
 *   `companyReply` / `owner_response` field per Story 23.5 documented API
 *   constraint; the 2-platform scope is STRUCTURALLY enforced by the
 *   absence of reply metrics on the Yelp adapter, NOT a product decision).
 *
 * @see `_bmad-output/implementation-artifacts/23-7-reviews-unified-widget.md` AC #1, #2
 * @see `_bmad-output/implementation-artifacts/23-8-review-response-tracking.md` AC #1, #2, #3
 */

import type { DimensionDefinition, MetricDefinition } from "../adapter";

/**
 * Sentinel `apiField` for the reviews cross-platform synthetic aggregates.
 * The build-time `metric-allowlist.test.ts` invariant exempts metrics with
 * this apiField from the wire-field-must-match-API-field check, parallel to
 * Story 20.9's `_synthetic_cross_tool_aggregate` convention.
 */
export const REVIEWS_CROSS_PLATFORM_SYNTHETIC_API_FIELD =
  "_synthetic_reviews_cross_platform_aggregate";

/**
 * 3 synthetic-aggregate metric definitions covering the cross-platform
 * reviews surface. Story 23.7 ships ONLY these 3 — every per-platform wire
 * metric remains in its own `GOOGLE_REVIEWS_METRICS` / `TRUSTPILOT_METRICS` /
 * `YELP_METRICS` array unchanged.
 *
 * The 3rd metric (`reviewsUnifiedSentimentStack`) is necessary because no
 * per-platform widget exposes the cross-platform sentiment composition —
 * even though sentiment is platform-specific, the comparison BETWEEN
 * platforms is the new signal.
 */
export const REVIEWS_CROSS_PLATFORM_METRICS: MetricDefinition[] = [
  {
    key: "reviewsUnifiedReputationScoreboard",
    label: "Unified Reputation Scoreboard (3-Platform)",
    type: "number",
    category: "reviews-cross-platform",
    dataType: "Decimal",
    // Story 31-31 — per-platform average-rating composite; no single volume weight (unweighted-mean fallback).
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: REVIEWS_CROSS_PLATFORM_SYNTHETIC_API_FIELD,
    description:
      "Synthetic — 3-element array { platform: 'google-reviews'|'trustpilot'|'yelp', rating: 0-5 decimal, totalReviews: integer, label: string } computed at widget-expand-time from each platform's headline rating + total reviews. Industry-canonical answer to 'what's our reputation across all platforms?' All 3 platforms use the same 0-5 decimal scale — no normalization needed.",
  },
  {
    // Round 3 Code Review Edge Case Hunter Finding R3-EH-04 + Blind
    // Hunter R3-BH-07 fix — register the 4th aggregateKey emitted by
    // Preset #2 (Rating Bar Comparison) as a first-class metric so
    // the future production-path resolver can route by exact-key
    // lookup rather than relying on the `reviewsUnified*` literal-prefix
    // string match in widget-populate.ts. Same dataType + apiField as
    // the scoreboard (it surfaces the same 3-platform rating array);
    // the chart-shape variation lives in `compatibleWidgets`.
    key: "reviewsUnifiedRatingBarComparison",
    label: "Unified Rating Bar Comparison (3-Platform)",
    type: "number",
    category: "reviews-cross-platform",
    dataType: "Decimal",
    // Story 31-31 — chart-shape variant of the scoreboard rating composite (unweighted-mean fallback).
    aggregationKind: "ratio",
    compatibleWidgets: ["bar-chart"],
    apiField: REVIEWS_CROSS_PLATFORM_SYNTHETIC_API_FIELD,
    description:
      "Synthetic — same 3-element rating array as reviewsUnifiedReputationScoreboard but materialized as a bar chart with explicit 0-5 y-axis range. Round 3 disambiguation — Preset #2's chart-shape variant of the scoreboard metric.",
  },
  {
    key: "reviewsUnifiedReviewVelocityTrend",
    label: "Unified Review Velocity Trend (3-Platform)",
    type: "number",
    category: "reviews-cross-platform",
    dataType: "Decimal",
    // Story 31-31 — reviews/month velocity sums meaningfully across attached resources.
    aggregationKind: "additive",
    compatibleWidgets: ["line-chart"],
    apiField: REVIEWS_CROSS_PLATFORM_SYNTHETIC_API_FIELD,
    description:
      "Synthetic — 3-series time-windowed array { platform: 'google-reviews'|'trustpilot'|'yelp', monthLabel: 'YYYY-MM', velocity: reviews/month integer } computed at widget-expand-time from each platform's reviewVelocityPerMonth synthetic-aggregate over the last 6 months. Build-to-sell category-defining widget — no competitor reporting tool surfaces cross-platform velocity-over-time joined in one widget.",
  },
  {
    key: "reviewsUnifiedSentimentStack",
    label: "Unified Sentiment Stack (3-Platform)",
    type: "number",
    category: "reviews-cross-platform",
    dataType: "Integer",
    // Story 31-31 — positive/neutral/negative review counts sum across attached resources.
    aggregationKind: "additive",
    compatibleWidgets: ["bar-chart"],
    apiField: REVIEWS_CROSS_PLATFORM_SYNTHETIC_API_FIELD,
    description:
      "Synthetic — 9-element matrix { platform: 'google-reviews'|'trustpilot'|'yelp', positive: int, neutral: int, negative: int } computed at widget-expand-time from each platform's positive/neutral/negative review-count synthetic-aggregates. Per-adapter sentiment-bucketing rules are identical (4-5★ positive / 3★ neutral / 1-2★ negative) — Story 23.7 does NOT re-bucket. Yelp's 3-excerpt subset means its sentiment row may be skewed positive by Yelp's most-recent-excerpt bias (per Story 23.5 AC #3 documented behavior).",
  },
  // ── Story 23.8 — Response Tracking metrics (2-platform: GR + TP only) ──
  // Yelp Fusion ships no `companyReply` / `owner_response` field per Story
  // 23.5 documented API constraint. The 2-platform scope is STRUCTURALLY
  // enforced by the absence of reply metrics on the Yelp adapter.
  {
    key: "reviewsUnifiedReplyRateScoreboard",
    label: "Unified Reply Rate Scoreboard (2-Platform)",
    type: "number",
    category: "reviews-cross-platform",
    dataType: "Percentage",
    // Story 31-31 — per-platform reply-rate composite; per-platform weights differ (unweighted-mean fallback).
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: REVIEWS_CROSS_PLATFORM_SYNTHETIC_API_FIELD,
    description:
      "Synthetic — 2-element array { platform: 'google-reviews'|'trustpilot', replyRate: 0-100 percentage, label: string } computed at widget-expand-time from each platform's headline reply-rate metric (Google `replyRate` = APPROVED-state replies / fetchedReviewCount × 100; Trustpilot `companyReplyRate` = companyReply non-null / fetchedReviewCount × 100). 2-platform NOT 3-platform — Yelp Fusion ships no reply API surface per Story 23.5.",
  },
  {
    key: "reviewsUnifiedAvgResponseTimeBar",
    label: "Unified Avg Response Time Bar (2-Platform)",
    type: "number",
    category: "reviews-cross-platform",
    dataType: "Duration",
    // Story 31-31 — per-platform average-latency composite; no single volume weight (unweighted-mean fallback).
    aggregationKind: "ratio",
    compatibleWidgets: ["bar-chart"],
    apiField: REVIEWS_CROSS_PLATFORM_SYNTHETIC_API_FIELD,
    description:
      "Synthetic — 2-element array { platform: 'google-reviews'|'trustpilot', avgResponseTimeSeconds: integer, label: string } computed from each platform's avg-latency metric (Google `avgReplyLatencySeconds` + Trustpilot `avgCompanyReplyLatencySeconds`). Rendered as bar chart with hours on y-axis (renderer divides by 3600). Color bands: 0-2h excellent / 2-24h good / 24-72h fair / 72h+ poor per ReplyOnTheFly 2026 benchmark study.",
  },
  {
    key: "reviewsUnifiedReplyRateTrend",
    label: "Unified Reply Rate Trend (2-Platform, 6 months)",
    type: "number",
    category: "reviews-cross-platform",
    dataType: "Percentage",
    // Story 31-31 — per-platform reply-rate time series; per-platform weights differ (unweighted-mean fallback).
    aggregationKind: "ratio",
    compatibleWidgets: ["line-chart"],
    apiField: REVIEWS_CROSS_PLATFORM_SYNTHETIC_API_FIELD,
    description:
      "Synthetic — 2-series time-windowed array { platform: 'google-reviews'|'trustpilot', monthLabel: 'YYYY-MM', replyRate: percentage } computed at widget-expand-time over a fixed 6-month rolling window. Build-to-sell category-defining widget — no competitor reporting tool surfaces cross-platform reply-rate trend-over-time joined in one widget (verified May 2026 against AgencyAnalytics + Whatagraph + Databox + Birdeye + Podium + Reputation.com + ReviewTrackers + RightResponse AI).",
  },
  {
    key: "reviewsUnifiedAwaitingResponseQueue",
    label: "Unified Awaiting Response Queue (2-Platform)",
    type: "number",
    category: "reviews-cross-platform",
    dataType: "Integer",
    // Story 31-31 — unreplied-review counts sum across attached resources.
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: REVIEWS_CROSS_PLATFORM_SYNTHETIC_API_FIELD,
    description:
      "Synthetic — 2-element array { platform: 'google-reviews'|'trustpilot', unrepliedCount: integer, oldestUnrepliedDays: integer, label: string } computed at widget-expand-time. `unrepliedCount` is each platform's `unrepliedReviewCount` baseline (Google does NOT count PENDING-state replies as unreplied per `google-reviews.ts:1392`; Trustpilot counts companyReply === null). `oldestUnrepliedDays` is derived from the per-record drill-down — max(now - createdAt) over records without a reply.",
  },
];

/**
 * Set of synthetic-aggregate metric keys whose `apiField` is the
 * `_synthetic_reviews_cross_platform_aggregate` sentinel. Mirrors Story
 * 20.9's `SEO_CROSS_TOOL_SYNTHETIC_METRIC_KEYS` runtime-immutable Proxy
 * pattern (Pass 2 BH2-1 hardening + Pass 3 BH3-5 forEach-leak fix).
 *
 * **DO NOT add a metric here to "fix" a test failure.** This Set is
 * reserved for metrics that are genuinely computed at preset-expand time
 * from a cross-platform join, not from wire-format API fields.
 */
const REVIEWS_CROSS_PLATFORM_SYNTHETIC_METRIC_KEYS_INTERNAL = new Set<string>([
  // Story 23.7 — Reputation widgets (3-platform: GR + TP + Yelp)
  "reviewsUnifiedReputationScoreboard",
  "reviewsUnifiedRatingBarComparison",
  "reviewsUnifiedReviewVelocityTrend",
  "reviewsUnifiedSentimentStack",
  // Story 23.8 — Response Tracking widgets (2-platform: GR + TP only)
  "reviewsUnifiedReplyRateScoreboard",
  "reviewsUnifiedAvgResponseTimeBar",
  "reviewsUnifiedReplyRateTrend",
  "reviewsUnifiedAwaitingResponseQueue",
]);

const SET_MUTATION_METHODS = new Set<string>(
  ["add", "delete", "clear"] as const,
);

/**
 * Read-only Set proxy. Forwards reads to the underlying Set but throws on
 * `add`/`delete`/`clear` calls. Copied verbatim from Story 20.9's
 * `seo-cross-tool/metrics.ts:100-131` per Round 4 ADR-E — the proxy stays
 * co-located with each cross-platform package per the build-to-sell-
 * directive's "module self-documentation" principle. Future refactor can
 * extract a shared util when a 3rd cross-platform package appears.
 *
 * - `Set.prototype.size` is a getter, so the trap forwards reads against
 *   the underlying Set (not the proxy receiver) — otherwise V8 throws
 *   "Method get Set.prototype.size called on incompatible receiver".
 * - `forEach` is special-cased to substitute the proxy as the 3rd callback
 *   argument so consumers cannot escape the readonly contract through the
 *   `set` parameter (Pass 3 BH3-5 leak defense).
 */
function asReadonlySet<T>(source: Set<T>): ReadonlySet<T> {
  // `proxyRef` must be `let` because the Proxy closure below captures the
  // reference BEFORE the assignment at the end of this function; mirrors
  // Story 20.9's pattern.
  // eslint-disable-next-line prefer-const
  let proxyRef: ReadonlySet<T>;
  const proxy = new Proxy(source, {
    get(target, prop, _receiver) {
      if (typeof prop === "string" && SET_MUTATION_METHODS.has(prop)) {
        return () => {
          throw new TypeError(
            `Cannot mutate REVIEWS_CROSS_PLATFORM_SYNTHETIC_METRIC_KEYS — Set is read-only at runtime per build-to-sell-directive. Use REVIEWS_CROSS_PLATFORM_METRICS expansions instead.`,
          );
        };
      }
      if (prop === "forEach") {
        return (
          callback: (value: T, value2: T, set: ReadonlySet<T>) => void,
          thisArg?: unknown,
        ): void => {
          target.forEach((v) => {
            callback.call(thisArg, v, v, proxyRef);
          });
        };
      }
      const value = Reflect.get(target, prop, target);
      return typeof value === "function" ? value.bind(target) : value;
    },
  }) as ReadonlySet<T>;
  proxyRef = proxy;
  return proxy;
}

export const REVIEWS_CROSS_PLATFORM_SYNTHETIC_METRIC_KEYS: ReadonlySet<string> =
  asReadonlySet(REVIEWS_CROSS_PLATFORM_SYNTHETIC_METRIC_KEYS_INTERNAL);

/**
 * Cross-platform dimension definitions (Story 23.7).
 *
 * Only `platform` is exposed — every synthetic-aggregate metric in
 * `REVIEWS_CROSS_PLATFORM_METRICS` returns a per-platform row, and `platform`
 * is the discriminator the renderer uses to colour series and label axes.
 * The 3 underlying per-platform adapters retain their own dimension
 * catalogues (e.g. `sentimentBucket`) — Story 23.7 does NOT re-export them
 * here to keep the cross-platform surface narrow.
 *
 * Catalog parity: `_bmad/bmm/data/integration-metrics/reviews-cross-platform.md`
 * Dimensions table row #1.
 */
export const REVIEWS_CROSS_PLATFORM_DIMENSIONS: DimensionDefinition[] = [
  {
    key: "platform",
    label: "Platform",
    type: "Review",
    // Story 23.7 metrics cover all 3 platforms; Story 23.8 metrics cover
    // only google-reviews + trustpilot (Yelp Fusion ships no reply
    // surface). The dimension itself is shared; each metric's
    // `description` field discloses its platform-scope.
    exampleValues: ["google-reviews", "trustpilot", "yelp"],
  },
];
